import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

const RegistrationPageContext = createContext(null);
const MOVED_PARAM = "moved";

function announceMove() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(MOVED_PARAM)) return;
  toastSuccess("Registration moved. The spot on the old date is open again.");
  url.searchParams.delete(MOVED_PARAM);
  window.history.replaceState(window.history.state, "", url);
}

export function RegistrationPageProvider({ initialRegistration = null, paths, csrfToken, canManage, canResend, currencyCode, children }) {
  const [registration, setRegistration] = useState(initialRegistration);
  const [loadError, setLoadError] = useState(false);
  const mounted = useRef(false);

  const load = useCallback(() => (
    axios.get(paths.registration, { params: { format: "json" } })
      .then((resp) => {
        if (!mounted.current) return;
        setLoadError(false);
        setRegistration(resp.data);
      })
      .catch(() => { if (mounted.current) setLoadError(true); })
  ), [paths.registration]);

  useEffect(() => {
    mounted.current = true;
    announceMove();
    if (!initialRegistration) load();
    return () => { mounted.current = false; };
  }, []);

  const cancelRegistration = useCallback(async (reason) => {
    try {
      const resp = await axios.post(`${paths.registration}/cancel`, { cancellation_reason: reason }, { headers: { "X-CSRF-Token": csrfToken } });
      toastSuccess(resp.data?.refund_submitted
        ? "Refund submitted. The registration cancels and the spot opens once it processes."
        : "Registration canceled. The spot is open again.");
      if (mounted.current) setRegistration(resp.data);
    } catch (e) {
      toastError(e.response?.data?.errors?.base?.[0] || "Could not cancel the registration.");
      await load();
    }
  }, [paths.registration, csrfToken, load]);

  const resendConfirmation = useCallback(async () => {
    try {
      const resp = await axios.post(`${paths.registration}/resend_confirmation`, {}, { headers: { "X-CSRF-Token": csrfToken } });
      toastSuccess(`Confirmation sent to ${resp.data.email}.`);
    } catch (e) {
      toastError(e.response?.data?.errors?.base?.[0] || "Could not resend the confirmation.");
    }
  }, [paths.registration, csrfToken]);

  const onTransferred = useCallback((data) => {
    const movedPath = data?.dashboard?.path;
    if (movedPath && movedPath !== paths.registration) {
      window.location.assign(`${movedPath}?${MOVED_PARAM}=1`);
      return;
    }
    load();
  }, [paths.registration, load]);

  const value = useMemo(() => ({
    registration,
    loadError,
    paths,
    csrfToken,
    canManage,
    canResend,
    currencyCode,
    cancelRegistration,
    resendConfirmation,
    onTransferred,
  }), [registration, loadError, paths, csrfToken, canManage, canResend, currencyCode, cancelRegistration, resendConfirmation, onTransferred]);

  return <RegistrationPageContext.Provider value={value}>{children}</RegistrationPageContext.Provider>;
}

RegistrationPageProvider.propTypes = {
  initialRegistration: PropTypes.object,
  paths: PropTypes.shape({
    registration: PropTypes.string.isRequired,
    classInstance: PropTypes.string.isRequired,
  }).isRequired,
  csrfToken: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  canResend: PropTypes.bool,
  currencyCode: PropTypes.string,
  children: PropTypes.node,
};

export function useRegistrationPageContext() {
  return useContext(RegistrationPageContext);
}
